import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { ReactFlow, Controls, Background, MiniMap, useNodesState, useEdgesState, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { analyzeConditionalGroups, getEffectiveCondition } from '../utils/dataConverters';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  flowContainer: {
    flex: 1,
    background: '#f8fafc',
    minHeight: '600px',
    '& .react-flow': {
      width: 'calc(100vw - 430px) !important',
      height: 'calc(100vh - 250px) !important',
    },
    '& .react-flow__edge-path': {
      stroke: '#64748b',
      strokeWidth: 2,
    },
    '& .react-flow__edge.animated': {
      strokeDasharray: '5 5',
    },
    '& .react-flow__handle': {
      width: 10,
      height: 10,
      background: '#64748b',
      border: '2px solid white',
    },
  },
}));

// Enhanced question node with beautiful design
const EnhancedQuestionNode = ({ data, isConnectable }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const nodeRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nodeRef.current && !nodeRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const getTypeColor = (type) => {
    const colors = {
      group: '#6366f1',
      grid: '#8b5cf6',
      grid_item: '#a855f7',
      checkbox: '#f59e0b',
      checkbox_item: '#fb923c',
      radio: '#10b981',
      pull_from_db: '#ef4444',
      number: '#3b82f6',
      textarea: '#64748b',
    };
    return colors[type] || '#64748b';
  };

  // Use the conditional status passed from flow generation (more reliable)
  const isConditional = data.isConditional || data.showIf || data.automation;

  return (
    <div
      ref={nodeRef}
      style={{
        background: isConditional ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : 'white',
        border: `2px solid ${isConditional ? '#3b82f6' : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: '16px',
        minWidth: '280px',
        maxWidth: '320px',
        boxShadow: isConditional
          ? '0 4px 12px rgba(59, 130, 246, 0.15)'
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease',
        position: 'relative',
      }}>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        style={{ top: -5 }}
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{
            color: '#1e293b',
            fontSize: '16px',
          }}>
            Q{String(data.number)}
          </strong>
          {isConditional && (
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
            }}>
              CONDITIONAL
            </span>
          )}
        </div>
        <span style={{
          background: getTypeColor(data.type),
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 'bold',
        }}>
          {data.type}
        </span>
      </div>

      {/* Question text */}
      <div style={{
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.4',
        marginBottom: '12px',
      }}>
        {data.text}
      </div>

      {/* Options preview */}
      {data.options && data.options.length > 0 && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '6px',
          padding: '8px',
          marginBottom: '8px',
        }}>
          <div style={{
            fontSize: '11px',
            color: '#64748b',
            fontWeight: '500',
            marginBottom: '4px',
          }}>
            Options ({data.options.length}):
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}>
            {data.options.slice(0, 3).map((option, idx) => (
              <div key={idx} style={{
                fontSize: '10px',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <span style={{
                  background: '#e2e8f0',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  fontWeight: 'bold',
                  minWidth: '12px',
                  textAlign: 'center',
                }}>
                  {option.value}
                </span>
                <span>{option.label}</span>
              </div>
            ))}
            {data.options.length > 3 && (
              <span style={{
                color: '#94a3b8',
                fontSize: '11px',
                fontStyle: 'italic',
              }}>
                +{data.options.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sub-questions preview */}
      {data.childQuestions && data.childQuestions.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
              borderRadius: '6px',
              padding: '8px',
              marginBottom: '8px',
              cursor: data.childQuestions.length > 3 ? 'pointer' : 'default',
              transition: 'background 0.2s ease',
            }}
            onClick={() => data.childQuestions.length > 3 && setShowTooltip(!showTooltip)}
          >
            <div style={{
              fontSize: '11px',
              color: '#64748b',
              fontWeight: '500',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <span style={{ fontSize: '14px' }}>📋</span>
              Sub-questions ({data.childQuestions.length}):
              {data.childQuestions.length > 3 && (
                <span style={{
                  fontSize: '9px',
                  color: '#94a3b8',
                  marginLeft: 'auto'
                }}>
                  (click to see all)
                </span>
              )}
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
            }}>
              {data.childQuestions.slice(0, 3).map((child, idx) => (
                <div key={idx} style={{
                  fontSize: '10px',
                  color: '#64748b',
                  paddingLeft: '12px',
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: '#94a3b8',
                  }}>
                    •
                  </span>
                  <span style={{
                    fontWeight: '500',
                    color: '#475569',
                  }}>
                    {child.number}:
                  </span> {child.text.length > 40 ? child.text.substring(0, 40) + '...' : child.text}
                </div>
              ))}
              {data.childQuestions.length > 3 && (
                <span style={{
                  color: '#94a3b8',
                  fontSize: '10px',
                  fontStyle: 'italic',
                  paddingLeft: '12px',
                }}>
                  +{data.childQuestions.length - 3} more questions
                </span>
              )}
            </div>
          </div>

          {/* Tooltip with all sub-questions */}
          {showTooltip && data.childQuestions.length > 3 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px',
              minWidth: '300px',
              maxWidth: '400px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
              zIndex: 1000,
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#1e293b',
                marginBottom: '8px',
                borderBottom: '1px solid #e2e8f0',
                paddingBottom: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>All Sub-questions ({data.childQuestions.length})</span>
                <button
                  onClick={() => setShowTooltip(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    fontSize: '16px',
                    lineHeight: 1,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#64748b';
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}>
                {data.childQuestions.map((child, idx) => (
                  <div key={idx} style={{
                    fontSize: '11px',
                    color: '#475569',
                    padding: '4px 0',
                    borderBottom: idx < data.childQuestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}>
                    <div style={{
                      fontWeight: '500',
                      color: '#1e293b',
                      marginBottom: '2px',
                    }}>
                      {child.number}. {child.type && (
                        <span style={{
                          fontSize: '9px',
                          background: '#e3f2fd',
                          color: '#1976d2',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          marginLeft: '4px',
                        }}>
                          {child.type}
                        </span>
                      )}
                    </div>
                    <div>{child.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Condition info */}
      {data.showIf && (
        <div style={{
          marginTop: '8px',
          padding: '6px',
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
          fontSize: '11px',
          color: '#92400e',
          fontStyle: 'italic',
        }}>
          Show if: {data.showIf}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        style={{ bottom: -5 }}
      />
    </div>
  );
};

// Decision diamond node
const DecisionNode = ({ data, isConnectable }) => {
  return (
    <div style={{
      width: '200px',
      height: '200px',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '3px solid #f59e0b',
      borderRadius: '24px',
      transform: 'rotate(45deg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
      position: 'relative',
    }}>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        style={{ top: -5, left: '50%', transform: 'translateX(-50%) rotate(-45deg)' }}
      />

      <div style={{
        transform: 'rotate(-45deg)',
        textAlign: 'center',
        padding: '20px',
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#92400e',
          marginBottom: '8px',
          lineHeight: '1.2',
        }}>
          {data.label}
        </div>
        {data.details && (
          <div style={{
            fontSize: '10px',
            color: '#b45309',
            lineHeight: '1.3',
            whiteSpace: 'pre-line',
          }}>
            {data.details}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="yes"
        isConnectable={isConnectable}
        style={{
          top: '50%',
          right: -5,
          transform: 'translateY(-50%) rotate(-45deg)',
          background: '#10b981',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="no"
        isConnectable={isConnectable}
        style={{
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%) rotate(-45deg)',
          background: '#ef4444',
        }}
      />
    </div>
  );
};

// Start/End nodes
const StartEndNode = ({ data }) => {
  const isEnd = data.label === 'END';

  return (
    <div style={{
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      background: isEnd
        ? 'linear-gradient(135deg, #ef4444, #dc2626)'
        : 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 'bold',
      boxShadow: `0 8px 24px ${isEnd ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`,
      border: '4px solid white',
    }}>
      {data.label}
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ bottom: -5 }}
        />
      )}
      {isEnd && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ top: -5 }}
        />
      )}
    </div>
  );
};

const nodeTypes = {
  question: EnhancedQuestionNode,
  decision: DecisionNode,
  startEnd: StartEndNode,
};

const SurveyFlow = ({ surveyData }) => {
  const classes = useStyles();

  // Helper function to estimate node height based on content (from original)
  const estimateNodeHeight = (question, childCount) => {
    let baseHeight = 80; // Base padding and header

    // Add height for question text
    const textLength = question.text?.length || 0;
    const textLines = Math.ceil(textLength / 45); // Approximate 45 chars per line
    baseHeight += textLines * 21; // 21px per line (14px font + line height)

    // Add height for options preview if exists
    if (question.options && question.options.length > 0) {
      baseHeight += 65; // Options section height with padding
    }

    // Add height for sub-questions preview
    if (childCount > 0) {
      const subQuestionsToShow = Math.min(childCount, 3);
      baseHeight += 40 + (subQuestionsToShow * 16); // Header + lines
      if (childCount > 3) {
        baseHeight += 16; // "+X more" line
      }
    }

    // Add height for condition info
    if (question.showIf) {
      baseHeight += 40; // Condition section height
    }

    return baseHeight;
  };

  // Generate nodes and edges - NEW CORRECT LOGIC
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!surveyData) {
      console.log('SurveyFlow: No survey data provided');
      return { nodes: [], edges: [] };
    }

    const nodes = [];
    const edges = [];
    const { questions } = surveyData;

    if (!questions || questions.length === 0) {
      return { nodes, edges };
    }

    // Configuration (matching original)
    const config = {
      nodeSpacing: 100, // Space between nodes (increased from 50)
      xBase: 400, // Main flow X position
      conditionalXOffset: 250, // Offset for conditional branches
      decisionXOffset: -50, // Offset for decision nodes (far left side)
      startY: 150, // Start position after START node
      decisionSpacingBefore: 80, // Extra space before decision node
      decisionSpacingAfter: 250, // Space after decision node for the diamond
    };

    // Start node
    nodes.push({
      id: 'start',
      type: 'startEnd',
      data: { label: 'START' },
      position: { x: config.xBase, y: 0 },
    });

    // Filter main flow questions (exclude sub-items)
    const mainQuestions = questions.filter(q =>
      q.type !== 'grid_item' &&
      q.type !== 'checkbox_item' &&
      !q.parentGrid &&
      !q.parentCheckbox
    );

    // Sort questions by number to ensure proper flow
    const sortedQuestions = mainQuestions.sort((a, b) => {
      const aNum = parseFloat(String(a.number).split('.')[0]) || 0;
      const bNum = parseFloat(String(b.number).split('.')[0]) || 0;
      return aNum - bNum;
    });

    let yPosition = config.startY;
    let lastNodeInMainFlow = 'start'; // Track last node in main flow (regular questions or decision nodes)

    // Analyze conditional groups to get effective conditions (universal approach)
    const effectiveConditions = analyzeConditionalGroups(surveyData);

    // Helper function to get effective condition for a question (using universal logic)
    const getQuestionEffectiveCondition = (question) => {
      return getEffectiveCondition(question, effectiveConditions);
    };

    // First pass: create all question nodes with space for decision nodes
    sortedQuestions.forEach((question, index) => {
      // Calculate child questions
      const childQuestions = questions.filter(q =>
        q.parentGrid === question.id ||
        q.parentCheckbox === question.id ||
        (q.id && q.id.startsWith(question.id + '.'))
      );
      const childCount = childQuestions.length;

      const effectiveCondition = getQuestionEffectiveCondition(question);
      const isConditional = Boolean(effectiveCondition);

      // Check if this is the first question in a new conditional group
      const isFirstInConditionalGroup = isConditional && (
        index === 0 ||
        !getQuestionEffectiveCondition(sortedQuestions[index - 1]) ||
        getQuestionEffectiveCondition(sortedQuestions[index - 1]) !== effectiveCondition
      );

      // Add extra space before conditional groups for decision node
      if (isFirstInConditionalGroup) {
        yPosition += 250; // Space for decision node
      }

      // Determine position
      const xPosition = isConditional ?
        config.xBase + config.conditionalXOffset :
        config.xBase;

      // Add question node
      nodes.push({
        id: question.id,
        type: 'question',
        data: {
          ...question,
          childCount: childCount,
          isConditional: isConditional, // Pass conditional status to node
          effectiveCondition: effectiveCondition, // Pass effective condition for display
          childQuestions: childQuestions.map(q => ({
            id: q.id,
            number: q.number,
            text: q.text,
            type: q.type
          }))
        },
        position: { x: xPosition, y: yPosition },
      });

      // Calculate next Y position using precise height estimation
      const nodeHeight = estimateNodeHeight(question, childCount);
      yPosition += nodeHeight + config.nodeSpacing;
    });

    // Analyze conditional groups (questions with same condition that go together)
    const conditionalGroups = [];
    let currentGroup = null;

    sortedQuestions.forEach((question, index) => {
      const effectiveCondition = getQuestionEffectiveCondition(question);
      const isConditional = Boolean(effectiveCondition);

      if (isConditional) {
        // Check if same condition as current group
        if (currentGroup && currentGroup.condition === effectiveCondition) {
          // Add to current group
          currentGroup.questions.push(question);
          currentGroup.lastIndex = index;
        } else {
          // Start new conditional group
          currentGroup = {
            condition: effectiveCondition,
            questions: [question],
            firstIndex: index,
            lastIndex: index,
            decisionId: `decision-${question.id}`
          };
          conditionalGroups.push(currentGroup);
        }
      } else {
        // End current conditional group
        currentGroup = null;
      }
    });

    // Create decision nodes for each conditional group in reserved space
    conditionalGroups.forEach((group) => {
      const firstQuestion = group.questions[0];
      const questionNode = nodes.find(n => n.id === firstQuestion.id);
      const decisionY = questionNode.position.y - 220; // Place in reserved space above first conditional question

      // Add decision node
      nodes.push({
        id: group.decisionId,
        type: 'decision',
        data: {
          label: 'Condition\nCheck',
          details: group.condition.length > 50 ?
            group.condition.substring(0, 50) + '...' :
            group.condition
        },
        position: {
          x: config.xBase + config.decisionXOffset,
          y: decisionY
        },
      });
    });

    // Second pass: create all connections based on correct flow logic
    lastNodeInMainFlow = 'start';

    sortedQuestions.forEach((question, index) => {
      const effectiveCondition = getQuestionEffectiveCondition(question);
      const isConditional = Boolean(effectiveCondition);

      if (isConditional) {
        // Find the conditional group for this question
        const group = conditionalGroups.find(g =>
          g.questions.some(q => q.id === question.id)
        );

        if (group && question.id === group.questions[0].id) {
          // This is the first question in a conditional group

          // Connect last node in main flow to decision node
          edges.push({
            id: `e-${lastNodeInMainFlow}-${group.decisionId}`,
            source: lastNodeInMainFlow,
            sourceHandle: lastNodeInMainFlow.startsWith('decision-') ? 'no' : undefined,
            target: group.decisionId,
            label: lastNodeInMainFlow.startsWith('decision-') ? 'NO' : undefined,
            style: lastNodeInMainFlow.startsWith('decision-') ?
              { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' } :
              { stroke: '#64748b', strokeWidth: 2 },
            animated: lastNodeInMainFlow.startsWith('decision-'),
          });

          // Connect decision to first conditional question (YES path)
          edges.push({
            id: `e-${group.decisionId}-${question.id}`,
            source: group.decisionId,
            sourceHandle: 'yes',
            target: question.id,
            label: 'YES',
            style: { stroke: '#10b981', strokeWidth: 2 },
            animated: true,
          });

          // Decision node becomes the last node in main flow
          lastNodeInMainFlow = group.decisionId;

        } else if (group) {
          // This is a continuation of conditional chain (connect from previous question in same group)
          const prevQuestionIndex = group.questions.findIndex(q => q.id === question.id) - 1;
          if (prevQuestionIndex >= 0) {
            const prevQuestion = group.questions[prevQuestionIndex];
            edges.push({
              id: `e-${prevQuestion.id}-${question.id}`,
              source: prevQuestion.id,
              target: question.id,
              style: { stroke: '#3b82f6', strokeWidth: 2 },
            });
          }
        }

        // If this is the last question in the conditional group, connect to next node
        if (group && question.id === group.questions[group.questions.length - 1].id) {
          const nextQuestion = sortedQuestions[index + 1];
          if (nextQuestion) {
            // Check if next question is conditional (has its own decision node)
            const nextGroup = conditionalGroups.find(g =>
              g.questions.some(q => q.id === nextQuestion.id)
            );

            if (nextGroup && nextQuestion.id === nextGroup.questions[0].id) {
              // Next question is conditional - connect to its decision node
              edges.push({
                id: `e-${question.id}-${nextGroup.decisionId}`,
                source: question.id,
                target: nextGroup.decisionId,
                style: { stroke: '#3b82f6', strokeWidth: 2 },
              });
            } else {
              // Next question is regular - connect directly
              edges.push({
                id: `e-${question.id}-${nextQuestion.id}`,
                source: question.id,
                target: nextQuestion.id,
                style: { stroke: '#3b82f6', strokeWidth: 2 },
              });
            }
          }
        }

      } else {
        // Regular question
        if (lastNodeInMainFlow !== 'start' && !lastNodeInMainFlow.startsWith('decision-')) {
          // Connect from previous regular question
          edges.push({
            id: `e-${lastNodeInMainFlow}-${question.id}`,
            source: lastNodeInMainFlow,
            target: question.id,
            style: { stroke: '#64748b', strokeWidth: 2 },
          });
        } else if (lastNodeInMainFlow.startsWith('decision-')) {
          // Connect from decision node (NO path) - this happens when conditional block ends
          edges.push({
            id: `e-${lastNodeInMainFlow}-no-${question.id}`,
            source: lastNodeInMainFlow,
            sourceHandle: 'no',
            target: question.id,
            label: 'NO',
            style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
            animated: true,
          });
        } else if (lastNodeInMainFlow === 'start') {
          // Connect from start
          edges.push({
            id: `e-start-${question.id}`,
            source: 'start',
            target: question.id,
            style: { stroke: '#64748b', strokeWidth: 2 },
          });
        }

        lastNodeInMainFlow = question.id;
      }
    });

    // End node - connect from last main flow node
    if (sortedQuestions.length > 0) {
      nodes.push({
        id: 'end',
        type: 'startEnd',
        data: { label: 'END' },
        position: { x: config.xBase, y: yPosition },
      });

      edges.push({
        id: `e-${lastNodeInMainFlow}-end`,
        source: lastNodeInMainFlow,
        sourceHandle: lastNodeInMainFlow.startsWith('decision-') ? 'no' : undefined,
        target: 'end',
        label: lastNodeInMainFlow.startsWith('decision-') ? 'NO' : undefined,
        style: lastNodeInMainFlow.startsWith('decision-') ?
          { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' } :
          { stroke: '#64748b', strokeWidth: 2 },
        animated: lastNodeInMainFlow.startsWith('decision-'),
      });
    }

    console.log('SurveyFlow: Generated', nodes.length, 'nodes and', edges.length, 'edges');
    return { nodes, edges };
  }, [surveyData]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  if (!surveyData) {
    return (
      <div className={classes.root}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          color: '#666'
        }}>
          No survey data available
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.flowContainer}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView={false}
          defaultViewport={{ x: 50, y: 50, zoom: 0.8 }}
          attributionPosition="top-right"
          panOnDrag={true}
          zoomOnScroll={true}
          preventScrolling={false}
        >
          <Controls />
          <MiniMap
            nodeColor="#94a3b8"
            nodeStrokeColor="#64748b"
            nodeStrokeWidth={2}
            maskColor="rgba(0, 0, 0, 0.2)"
            pannable={true}
            zoomable={true}
          />
          <Background variant="dots" gap={20} size={1} />
        </ReactFlow>
      </div>
    </div>
  );
};

export default SurveyFlow;
